import { useState, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";

export interface VariantData {
  readonly id?: string;
  readonly sku: string;
  readonly option1Value: string;
  readonly option2Value: string;
  readonly price: string;
  readonly compareAtPrice: string;
  readonly costPrice: string;
  readonly stockQuantity: string;
  readonly lowStockThreshold: string;
  readonly weightGrams: string;
  readonly barcode: string;
}

interface VariantEditorProps {
  readonly option1Name: string;
  readonly option2Name: string;
  readonly onOption1NameChange: (v: string) => void;
  readonly onOption2NameChange: (v: string) => void;
  readonly variants: readonly VariantData[];
  readonly onVariantsChange: (variants: readonly VariantData[]) => void;
}

function emptyVariant(): VariantData {
  return {
    sku: "", option1Value: "", option2Value: "",
    price: "", compareAtPrice: "", costPrice: "",
    stockQuantity: "0", lowStockThreshold: "5",
    weightGrams: "", barcode: "",
  };
}

export function VariantEditor({
  option1Name, option2Name,
  onOption1NameChange, onOption2NameChange,
  variants, onVariantsChange,
}: VariantEditorProps): ReactNode {
  const [opt1Input, setOpt1Input] = useState("");
  const [opt2Input, setOpt2Input] = useState("");

  const generateGrid = useCallback(() => {
    const opt1Values = opt1Input.split(",").map((v) => v.trim()).filter(Boolean);
    const opt2Values = opt2Input.split(",").map((v) => v.trim()).filter(Boolean);

    if (opt1Values.length === 0) return;

    const generated: VariantData[] = [];
    for (const v1 of opt1Values) {
      if (opt2Values.length > 0) {
        for (const v2 of opt2Values) {
          // Preserve existing variant data if option combo already exists
          const existing = variants.find(
            (ev) => ev.option1Value === v1 && ev.option2Value === v2,
          );
          generated.push(existing ?? { ...emptyVariant(), option1Value: v1, option2Value: v2 });
        }
      } else {
        const existing = variants.find((ev) => ev.option1Value === v1 && !ev.option2Value);
        generated.push(existing ?? { ...emptyVariant(), option1Value: v1 });
      }
    }
    onVariantsChange(generated);
  }, [opt1Input, opt2Input, variants, onVariantsChange]);

  const updateVariant = useCallback(
    (index: number, field: keyof VariantData, value: string) => {
      onVariantsChange(
        variants.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
      );
    },
    [variants, onVariantsChange],
  );

  const removeVariant = useCallback(
    (index: number) => {
      onVariantsChange(variants.filter((_, i) => i !== index));
    },
    [variants, onVariantsChange],
  );

  const addVariant = useCallback(() => {
    onVariantsChange([...variants, emptyVariant()]);
  }, [variants, onVariantsChange]);

  return (
    <div className="space-y-4">
      {/* Option dimensions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium">{"\u05DE\u05D9\u05DE\u05D3 1 (\u05DC\u05DE\u05E9\u05DC: \u05DE\u05D9\u05D3\u05D4)"}</label>
          <Input
            value={option1Name}
            onChange={(e) => onOption1NameChange(e.target.value)}
            placeholder={"\u05DE\u05D9\u05D3\u05D4"}
            className="mt-1"
          />
          <Input
            value={opt1Input}
            onChange={(e) => setOpt1Input(e.target.value)}
            placeholder={"S, M, L, XL"}
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">{"\u05D4\u05E4\u05E8\u05D3 \u05E2\u05DD \u05E4\u05E1\u05D9\u05E7\u05D9\u05DD"}</p>
        </div>
        <div>
          <label className="text-sm font-medium">{"\u05DE\u05D9\u05DE\u05D3 2 (\u05DC\u05DE\u05E9\u05DC: \u05E6\u05D1\u05E2)"}</label>
          <Input
            value={option2Name}
            onChange={(e) => onOption2NameChange(e.target.value)}
            placeholder={"\u05E6\u05D1\u05E2"}
            className="mt-1"
          />
          <Input
            value={opt2Input}
            onChange={(e) => setOpt2Input(e.target.value)}
            placeholder={"\u05D0\u05D3\u05D5\u05DD, \u05DB\u05D7\u05D5\u05DC, \u05DC\u05D1\u05DF"}
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">{"\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9"}</p>
        </div>
      </div>

      <Button type="button" variant="outline" size="sm" onClick={generateGrid}>
        {"\u05D9\u05E6\u05D9\u05E8\u05EA \u05D8\u05D1\u05DC\u05EA \u05D5\u05E8\u05D9\u05D0\u05E0\u05D8\u05D9\u05DD"}
      </Button>

      {/* Variants table */}
      {variants.length > 0 && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-muted/50">
                <tr>
                  {option1Name && <th className="p-2 text-start">{option1Name}</th>}
                  {option2Name && <th className="p-2 text-start">{option2Name}</th>}
                  <th className="p-2 text-start">{"SKU"}</th>
                  <th className="p-2 text-start">{"\u05DE\u05D7\u05D9\u05E8 (\u20AA)"}</th>
                  <th className="p-2 text-start">{"\u05DE\u05DC\u05D0\u05D9"}</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v, i) => (
                  <tr key={`${v.option1Value}-${v.option2Value}-${i}`} className="border-t">
                    {option1Name && (
                      <td className="p-2">
                        <Input
                          value={v.option1Value}
                          onChange={(e) => updateVariant(i, "option1Value", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </td>
                    )}
                    {option2Name && (
                      <td className="p-2">
                        <Input
                          value={v.option2Value}
                          onChange={(e) => updateVariant(i, "option2Value", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </td>
                    )}
                    <td className="p-2">
                      <Input
                        value={v.sku}
                        onChange={(e) => updateVariant(i, "sku", e.target.value)}
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={v.price}
                        onChange={(e) => updateVariant(i, "price", e.target.value)}
                        placeholder={"\u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC"}
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        min="0"
                        value={v.stockQuantity}
                        onChange={(e) => updateVariant(i, "stockQuantity", e.target.value)}
                        className="h-8 text-sm"
                      />
                    </td>
                    <td className="p-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => removeVariant(i)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Button type="button" variant="ghost" size="sm" onClick={addVariant}>
        <Plus className="h-3.5 w-3.5 me-1" />
        {"\u05D4\u05D5\u05E1\u05E4\u05EA \u05D5\u05E8\u05D9\u05D0\u05E0\u05D8"}
      </Button>
    </div>
  );
}
